import AdminLayout from '@/components/admin/AdminLayout'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { useOrder, useOrders, type OrderStatus } from '@/queries/useOrders'
import { useSelectionStore } from '@/store/selectionStore'
import { useUser } from '@clerk/clerk-react'
import { format, formatDistanceToNow } from 'date-fns'
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    CheckCircle2,
    ChefHat,
    CircleCheckBig,
    FileText,
    RefreshCw,
    Search,
    Send,
    XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const statusColors: Record<OrderStatus, string> = {
  draft: 'bg-gray-500',
  submitted: 'bg-blue-500',
  preparing: 'bg-orange-500',
  ready: 'bg-green-500',
  completed: 'bg-green-600',
  cancelled: 'bg-red-500',
}

// Status labels and tooltips will be translated in the component

function formatPrice(piasters: number): string {
  return `${(piasters / 100).toFixed(1)} EGP`
}

export default function Orders() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { user, isLoaded: isUserLoaded } = useUser()
  const { selectedBranch } = useSelectionStore()
  const selectedBranchId = selectedBranch?.id

  // Status labels and tooltips with translations
  const statusLabels: Record<OrderStatus, string> = {
    draft: t('orders.status.draft'),
    submitted: t('orders.status.submitted'),
    preparing: t('orders.status.preparing'),
    ready: t('orders.status.ready'),
    completed: t('orders.status.completed'),
    cancelled: t('orders.status.cancelled'),
  }

  const statusIcons: Record<OrderStatus, React.ReactNode> = {
    draft: <FileText className="h-3 w-3" />,
    submitted: <Send className="h-3 w-3" />,
    preparing: <ChefHat className="h-3 w-3" />,
    ready: <CheckCircle2 className="h-3 w-3" />,
    completed: <CircleCheckBig className="h-3 w-3" />,
    cancelled: <XCircle className="h-3 w-3" />,
  }

  const statusTooltips: Record<OrderStatus, string> = {
    draft: t('orders.status.tooltips.draft'),
    submitted: t('orders.status.tooltips.submitted'),
    preparing: t('orders.status.tooltips.preparing'),
    ready: t('orders.status.tooltips.ready'),
    completed: t('orders.status.tooltips.completed'),
    cancelled: t('orders.status.tooltips.cancelled'),
  }

  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sortBy, setSortBy] = useState<'createdAt' | 'status' | 'total'>(
    'createdAt'
  )
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [jumpToPageInput, setJumpToPageInput] = useState('')
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const { data: selectedOrderData } = useOrder(selectedOrderId || '')

  // Check for restaurant_admin, branch_manager, or super_admin role
  const userRoles = (user?.publicMetadata?.roles as string[] | undefined) || []
  const hasAccess =
    Array.isArray(userRoles) &&
    (userRoles.includes('restaurant_admin') ||
      userRoles.includes('branch_manager') ||
      userRoles.includes('super_admin'))

  // Redirect if user doesn't have required role
  useEffect(() => {
    if (isUserLoaded && !hasAccess) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [isUserLoaded, hasAccess, navigate])

  // Set default date range (last 7 days)
  useEffect(() => {
    const today = new Date()
    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    setDateFrom(format(sevenDaysAgo, 'yyyy-MM-dd'))
    setDateTo(format(today, 'yyyy-MM-dd'))
  }, [])

  const { data, isLoading, error, refetch } = useOrders({
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: searchQuery || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    pageSize,
    sortBy,
    sortOrder,
  })

  const handleSort = (field: 'createdAt' | 'status' | 'total') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(field)
      setSortOrder('DESC')
    }
  }

  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpToPageInput, 10)
    if (
      !isNaN(pageNum) &&
      pageNum >= 1 &&
      pageNum <= (data?.meta.totalPages || 1)
    ) {
      setPage(pageNum)
      setJumpToPageInput('')
    }
  }

  if (!hasAccess) {
    return null
  }

  if (!selectedBranchId) {
    return (
      <AdminLayout>
        <div className="p-6">
          <Alert>
            <AlertDescription>
              {t('orders.error.noBranchSelected')}
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1
            className="text-3xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            {t('orders.title')}
          </h1>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
            {t('orders.refresh')}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-medium mb-2 block">
              {t('orders.search')}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground rtl:left-auto rtl:right-3" />
              <Input
                placeholder={t('orders.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                className="pl-10 rtl:pl-0 rtl:pr-10"
              />
            </div>
          </div>

          <div className="min-w-[150px]">
            <label className="text-sm font-medium mb-2 block">
              {t('orders.status')}
            </label>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as OrderStatus | 'all')
                setPage(1)
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('orders.statusAll')}</SelectItem>
                <SelectItem value="draft">
                  {t('orders.status.draft')}
                </SelectItem>
                <SelectItem value="submitted">
                  {t('orders.status.submitted')}
                </SelectItem>
                <SelectItem value="preparing">
                  {t('orders.status.preparing')}
                </SelectItem>
                <SelectItem value="ready">
                  {t('orders.status.ready')}
                </SelectItem>
                <SelectItem value="completed">
                  {t('orders.status.completed')}
                </SelectItem>
                <SelectItem value="cancelled">
                  {t('orders.status.cancelled')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[150px]">
            <label className="text-sm font-medium mb-2 block">
              {t('orders.fromDate')}
            </label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value)
                setPage(1)
              }}
            />
          </div>

          <div className="min-w-[150px]">
            <label className="text-sm font-medium mb-2 block">
              {t('orders.toDate')}
            </label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value)
                setPage(1)
              }}
            />
          </div>

          <Button
            variant="outline"
            onClick={() => {
              setStatusFilter('all')
              setSearchQuery('')
              setDateFrom('')
              setDateTo('')
              setPage(1)
            }}
          >
            {t('orders.reset')}
          </Button>
        </div>

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{t('orders.error.loadFailed')}</AlertDescription>
          </Alert>
        )}

        {/* Orders Table */}
        {isLoading ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('orders.table.orderId')}</TableHead>
                <TableHead>{t('orders.table.status')}</TableHead>
                <TableHead className="text-right rtl:text-left">
                  {t('orders.table.total')}
                </TableHead>
                <TableHead>{t('orders.table.created')}</TableHead>
                <TableHead className="hidden md:table-cell">
                  {t('orders.table.submitted')}
                </TableHead>
                <TableHead>{t('orders.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell className="text-right rtl:text-left">
                    <Skeleton className="h-4 w-16 ml-auto rtl:ml-0 rtl:mr-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-24" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : !data || data.data.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {t('orders.emptyState')}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 -ml-3 rtl:ml-0 rtl:-mr-3"
                      onClick={() => handleSort('status')}
                    >
                      {t('orders.table.status')}
                      {sortBy === 'status' ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp className="ml-2 h-4 w-4 rtl:ml-0 rtl:mr-2" />
                        ) : (
                          <ArrowDown className="ml-2 h-4 w-4 rtl:ml-0 rtl:mr-2" />
                        )
                      ) : (
                        <ArrowUpDown className="ml-2 h-4 w-4 opacity-50 rtl:ml-0 rtl:mr-2" />
                      )}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right rtl:text-left">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 -ml-3 rtl:ml-0 rtl:-mr-3"
                      onClick={() => handleSort('total')}
                    >
                      {t('orders.table.total')}
                      {sortBy === 'total' ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp className="ml-2 h-4 w-4 rtl:ml-0 rtl:mr-2" />
                        ) : (
                          <ArrowDown className="ml-2 h-4 w-4 rtl:ml-0 rtl:mr-2" />
                        )
                      ) : (
                        <ArrowUpDown className="ml-2 h-4 w-4 opacity-50 rtl:ml-0 rtl:mr-2" />
                      )}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 -ml-3 rtl:ml-0 rtl:-mr-3"
                      onClick={() => handleSort('createdAt')}
                    >
                      {t('orders.table.created')}
                      {sortBy === 'createdAt' ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp className="ml-2 h-4 w-4 rtl:ml-0 rtl:mr-2" />
                        ) : (
                          <ArrowDown className="ml-2 h-4 w-4 rtl:ml-0 rtl:mr-2" />
                        )
                      ) : (
                        <ArrowUpDown className="ml-2 h-4 w-4 opacity-50 rtl:ml-0 rtl:mr-2" />
                      )}
                    </Button>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    {t('orders.table.submitted')}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    {t('orders.table.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((order) => (
                  <TableRow
                    key={order.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedOrderId(order.id)}
                  >
                    <TableCell className="font-mono text-sm">
                      {order.id.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              className={`${statusColors[order.status]} flex items-center gap-1 w-fit`}
                              variant="default"
                            >
                              {statusIcons[order.status]}
                              <span className="hidden sm:inline">
                                {statusLabels[order.status]}
                              </span>
                              <span className="sm:hidden">
                                {statusLabels[order.status].slice(0, 1)}
                              </span>
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{statusTooltips[order.status]}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPrice(order.total)}
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              {format(
                                new Date(order.createdAt),
                                'MMM d, yyyy HH:mm'
                              )}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {formatDistanceToNow(new Date(order.createdAt), {
                                addSuffix: true,
                              })}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {order.submittedAt ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                {format(
                                  new Date(order.submittedAt),
                                  'MMM d, yyyy HH:mm'
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {formatDistanceToNow(
                                  new Date(order.submittedAt),
                                  {
                                    addSuffix: true,
                                  }
                                )}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedOrderId(order.id)
                        }}
                      >
                        {t('orders.table.viewDetails')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {data.meta.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  {t('orders.pagination.showing', {
                    from:
                      data.meta.page * data.meta.pageSize -
                      data.meta.pageSize +
                      1,
                    to: Math.min(
                      data.meta.page * data.meta.pageSize,
                      data.meta.total
                    ),
                    total: data.meta.total,
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground">
                      {t('orders.pagination.itemsPerPage')}
                    </label>
                    <Select
                      value={pageSize.toString()}
                      onValueChange={(value) => {
                        setPageSize(parseInt(value, 10))
                        setPage(1)
                      }}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground">
                      {t('orders.pagination.jumpTo')}
                    </label>
                    <Input
                      type="number"
                      min={1}
                      max={data.meta.totalPages}
                      value={jumpToPageInput}
                      onChange={(e) => setJumpToPageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleJumpToPage()
                        }
                      }}
                      placeholder={t('orders.pagination.page')}
                      className="w-20 h-8"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleJumpToPage}
                      className="h-8"
                    >
                      {t('orders.pagination.go')}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8"
                    >
                      {t('orders.pagination.previous')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage((p) => Math.min(data.meta.totalPages, p + 1))
                      }
                      disabled={!data.meta.hasMore}
                      className="h-8"
                    >
                      {t('orders.pagination.next')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Order Details Modal */}
        <Dialog
          open={!!selectedOrderId}
          onOpenChange={(open) => !open && setSelectedOrderId(null)}
        >
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('orders.details.title')}</DialogTitle>
              <DialogDescription>
                {t('orders.details.orderId', { id: selectedOrderId })}
              </DialogDescription>
            </DialogHeader>
            {selectedOrderData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">
                      {t('orders.details.status')}
                    </label>
                    <div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              className={`${statusColors[selectedOrderData.status]} flex items-center gap-1 w-fit`}
                              variant="default"
                            >
                              {statusIcons[selectedOrderData.status]}
                              {statusLabels[selectedOrderData.status]}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{statusTooltips[selectedOrderData.status]}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      {t('orders.details.total')}
                    </label>
                    <div className="text-lg font-semibold">
                      {formatPrice(selectedOrderData.total)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      {t('orders.details.created')}
                    </label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">
                            {format(
                              new Date(selectedOrderData.createdAt),
                              'MMM d, yyyy HH:mm'
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {formatDistanceToNow(
                              new Date(selectedOrderData.createdAt),
                              {
                                addSuffix: true,
                              }
                            )}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {selectedOrderData.submittedAt && (
                    <div>
                      <label className="text-sm font-medium">
                        {t('orders.details.submitted')}
                      </label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="cursor-help">
                              {format(
                                new Date(selectedOrderData.submittedAt),
                                'MMM d, yyyy HH:mm'
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {formatDistanceToNow(
                                new Date(selectedOrderData.submittedAt),
                                {
                                  addSuffix: true,
                                }
                              )}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>

                {selectedOrderData.items &&
                  selectedOrderData.items.length > 0 && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('orders.details.items')}
                      </label>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('orders.details.item')}</TableHead>
                            <TableHead>
                              {t('orders.details.quantity')}
                            </TableHead>
                            <TableHead>
                              {t('orders.details.unitPrice')}
                            </TableHead>
                            <TableHead>
                              {t('orders.details.itemTotal')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedOrderData.items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                {item.menuItemName ||
                                  t('orders.details.unknownItem')}
                                {item.menuItemNameAr && (
                                  <div className="text-sm text-muted-foreground">
                                    {item.menuItemNameAr}
                                  </div>
                                )}
                                {item.specialInstructions && (
                                  <div className="text-sm text-muted-foreground mt-1 italic">
                                    {t('orders.details.note', {
                                      note: item.specialInstructions,
                                    })}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>
                                {formatPrice(item.unitPrice)}
                              </TableCell>
                              <TableCell>
                                {formatPrice(item.unitPrice * item.quantity)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
              </div>
            ) : (
              <div className="text-center py-8">
                {t('orders.details.loading')}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  )
}
